import type { Translation } from '../types';

/** Source of truth. Every other locale is checked against this shape. */
export const en: Translation = {
  meta: { name: 'English', english: 'English', tag: 'en', dir: 'ltr' },

  common: {
    menu: "Menu",
    cancel: 'Cancel',
    done: 'Done',
    join: 'Join',
    leave: 'Leave',
    refresh: 'Refresh',
    save: 'Save',
    you: 'you',
    host: 'host',
    ai: 'AI',
    ready: 'Ready',
    notReady: 'Not ready',
  },

  conn: {
    live: 'Live',
    connecting: 'Connecting',
    offline: 'Offline',
    connectingFull: 'Connecting…',
    reconnectBanner: 'Connection lost — reconnecting. Your seat is held for 3 minutes.',
  },

  nickname: {
    pickTitle: 'Pick a name',
    changeTitle: 'Change your name',
    subtitle: 'This is how other players will see you.',
    placeholder: 'e.g. Wild Four',
    label: 'Nickname',
    letsPlay: "Let's play",
  },

  lobby: {
    heroTitle: 'Play UNO with friends',
    heroSubtitle: 'No signup. Share a link and deal.',
    createRoom: 'Create room',
    roomCode: 'Room code',
    publicRooms: 'Public rooms',
    empty: 'No public rooms yet — create the first one.',
    cardMeta: 'host {host} · {seconds}s turns',
    stacking: 'stacking',
    badgeOpen: 'Open',
    badgeFull: 'Full',
    badgeLive: 'In game',
    lockedTitle: 'Room {id} is locked',
    password: 'Password',
    passwordLabel: 'Room password',
  },

  create: {
    title: 'New room',
    roomName: 'Room name',
    roomNamePlaceholder: 'Friday night UNO',
    maxPlayers: 'Max players',
    playersOption: { one: '{count} player', other: '{count} players' },
    passwordOptional: 'Password (optional)',
    passwordPlaceholder: 'Leave blank for open',
    listPublic: 'List in the public room list',
    houseRules: 'House rules',
    submit: 'Create',
  },

  rules: {
    turnTimer: 'Turn timer',
    turnTimerHint: 'When it runs out, a bot plays a random legal card for you.',
    startingHand: 'Starting hand',
    unoPenalty: 'UNO penalty',
    stackDrawTwo: '+2 stacking',
    stackDrawTwoHint: 'Answer a +2 with another +2 instead of drawing',
    stackDrawFour: '+4 stacking',
    stackDrawFourHint: 'Answer a +4 with another +4',
    challengeWildFour: '+4 challenge',
    challengeWildFourHint: 'The next player may call out an illegal +4',
    strictWildFour: 'Strict +4',
    strictWildFourHint: 'Only playable when you hold no card of the active colour',
    playDrawn: 'Play what you draw',
    playDrawnHint: 'A drawn card may be played straight away',
    drawUntilPlayable: 'Draw until playable',
    drawUntilPlayableHint: 'Keep drawing until you get a legal card',
    hostOnly: 'only the host can change these',
  },

  room: {
    copyLink: 'Copy invite link',
    copied: '✓ Copied',
    roomLabel: 'Room',
    locked: 'locked',
    public: 'public',
    unlisted: 'unlisted',
    readyStatus: '{ready} of {total} ready',
    botsJoining: { one: '{count} bot joining', other: '{count} bots joining' },
    startsWhenReady: 'the game starts the moment everyone is.',
    needMorePlayers: 'Needs {min} players to start — invite someone, or add a bot.',
    playersTitle: 'Players',
    botsTitle: 'AI bots',
    botsHostOnly: 'only the host can change this',
    noBots: 'No bots',
    botsWillJoin: { one: '{count} bot will join', other: '{count} bots will join' },
    botsHint: 'They take their seats when the game starts. Up to {max} fit right now.',
    botsRoomFull: 'The room is full — no space for bots.',
    soloCta: 'Playing alone? Add a bot and start',
    addBot: 'Add a bot',
    removeBot: 'Remove a bot',
    removePlayer: 'Remove {name}',
    botPlaceholder: 'Bot {n}',
    defaultRoomName: "{name}'s room",
    imReady: "I'm ready",
    cancelReady: 'Cancel ready',
    offlineTag: 'offline',
  },

  game: {
    yourTurn: 'Your turn',
    yourTurnStack: 'Your turn — stack or take +{count}',
    othersTurn: "{name}'s turn",
    waiting: 'Waiting…',
    gameOver: 'Game over',
    challengeWindow: 'Wild Draw Four — challenge window',
    drawPileLeft: '{count} left',
    discard: 'discard',
    draw: 'Draw',
    takeCards: 'Take +{count}',
    pass: 'Pass',
    uno: 'UNO!',
    judge: 'JUDGE',
    cardCount: { one: '{count} card', other: '{count} cards' },
    clockwise: 'Clockwise',
    counterClockwise: 'Counter-clockwise',
    activeColour: 'Active colour',
    noCards: 'No cards — nice.',
    oneCard: 'one card!',
    unoTag: 'UNO',
    forfeitedTag: 'forfeited',
    offlineFor: 'offline {seconds}s',
    placeTag: '#{place}',
    pickColour: 'Pick a colour',
    challengeTitle: '{name} played a Wild Draw Four. Challenge it?',
    challengeExplain: 'If they were bluffing they draw {bluff} instead. If not, you draw {fail}.',
    challengeBtn: 'Challenge',
    acceptBtn: 'Take +{count}',
    youWin: '🎉 You win!',
    standingsTitle: 'Game over',
    points: '{points} pts',
    forfeited: 'forfeited',
    backToLobby: 'Back to lobby',
    playAgain: 'Play again',
    waitingOthers: 'Waiting for others…',
    faceDownCard: 'Face-down card',
    sortByColour: "Sort by colour",
    sortAsDealt: "Keep the order dealt",
    drawACard: 'Draw a card',
    drawNCards: 'Draw {count} cards',
  },

  events: {
    someone: 'Someone',
    unoCalledYou: 'You called UNO!',
    unoCalledOther: '{name} called UNO!',
    judged: '{accuser} caught {target} — +{count} cards',
    judgedYou: '{accuser} caught you — +{count} cards',
    challengeBluff: 'Bluff caught — {name} draws {count}',
    challengeFailed: 'Challenge failed — {name} draws {count}',
    forfeited: '{name} forfeited.',
    afkYou: 'You ran out of time — auto-played.',
    afkOther: '{name} ran out of time — auto-played.',
    hostChanged: '{name} is now the host.',
    reshuffled: 'Draw pile reshuffled.',
    youWin: 'You win!',
    winner: '{name} wins!',
    gameOver: 'Game over.',
  },

  errors: {
    bad_request: 'Something went wrong.',
    no_session: 'Session expired — reconnecting.',
    room_not_found: 'That room does not exist.',
    room_full: 'That room is full.',
    wrong_password: 'Wrong password.',
    already_in_room: 'Leave your current room first.',
    not_in_room: 'You are not in a room.',
    not_host: 'Only the host can do that.',
    game_in_progress: 'That game has already started.',
    not_enough_players: 'Not enough players.',
    stale_action: 'Someone acted first — your move was discarded.',
    illegal_action: "That move isn't allowed.",
    rate_limited: 'Slow down.',
    nickname_invalid: 'That nickname will not work.',
  },

  theme: {
    title: 'Card theme',
    description: 'Missing images fall back to the built-in CSS deck.',
    builtIn: 'Built in — no assets required',
    pngHint: 'Drop 300×450 PNGs into {path} and register them in the theme index.',
  },

  language: {
    title: 'Language',
    description: 'Applies immediately, and is remembered on this device.',
  },

  cards: {
    red: "red",
    yellow: "yellow",
    green: "green",
    blue: "blue",
    wild: "wild",
    skip: "Skip",
    reverse: "Reverse",
    draw2: "+2",
    wildCard: "Wild",
    wild4: "+4",
  },

  log: {
    title: "Play log",
    empty: "Nothing yet.",
    close: "Hide log",
    timestamps: "Timestamps",
    chatPlaceholder: "Say something…",
    send: "Send",
    played: "{name} played {card}",
    drew: "{name} drew {count}",
    skipped: "{name} was skipped",
    reversed: "Direction reversed",
    joined: "{name} joined",
    left: "{name} left",
    started: "Game started",
  },

  profile: {
    avatar: "Avatar",
    avatarHint: "Pick an avatar, or keep your initial.",
  },

  leave: {
    title: "Leave the game?",
    body: "The round is still running. If you go, you forfeit and your cards return to the deck.",
    stay: "Keep playing",
    confirm: "Leave anyway",
    unload: "You are in the middle of a game.",
  },
};
